import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isManualOverride, setIsManualOverride] = useState(false);

  // Get the effective theme based on override or system preference
  const getEffectiveTheme = useCallback(() => {
    const savedTheme = localStorage.getItem('theme');

    // If user has manually set a preference, use it
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme === 'dark';
    }

    // Otherwise, follow system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  // Apply theme to document
  const applyTheme = useCallback((dark) => {
    if (dark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    setIsManualOverride(savedTheme === 'dark' || savedTheme === 'light');

    const dark = getEffectiveTheme();
    setIsDarkMode(dark);
    applyTheme(dark);
  }, [getEffectiveTheme, applyTheme]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Only react to system changes if no manual override
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme !== 'dark' && savedTheme !== 'light') {
        const dark = e.matches;
        setIsDarkMode(dark);
        applyTheme(dark);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [applyTheme]);

  // Toggle between light and dark (creates manual override)
  const toggleDarkMode = useCallback(() => {
    const newDark = !isDarkMode;
    setIsDarkMode(newDark);
    setIsManualOverride(true);
    applyTheme(newDark);
    localStorage.setItem('theme', newDark ? 'dark' : 'light');
  }, [isDarkMode, applyTheme]);

  // Reset to follow system preference
  const resetToSystem = useCallback(() => {
    localStorage.removeItem('theme');
    setIsManualOverride(false);
    const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(dark);
    applyTheme(dark);
  }, [applyTheme]);

  const value = {
    isDarkMode,
    isManualOverride,
    toggleDarkMode,
    resetToSystem,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};